import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdAccessTime,
  MdAssignment,
  MdAutorenew,
  MdCheckCircle,
  MdSelfImprovement,
} from "react-icons/md";
import Card from "components/card";
import Button from "components/button";
import { PageContainer, PageHeader } from "components/page";
import StatCard from "components/stat-card";
import Tabs, { SegmentedControl, TabPanel } from "components/tabs";
import { SearchInput, Select } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { useConfirm } from "components/dialog/ConfirmProvider";
import heartbeatActionItemService from "services/heartbeatActionItemService";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { formatNumber } from "utils/format";
import ActionItemRow from "./ActionItemRow";
import PrayerRequestRow from "./PrayerRequestRow";
import AssignStaffDialog from "./AssignStaffDialog";
import {
  DATE_SORT_OPTIONS,
  PRAYER_STATUS_OPTIONS,
  PRIORITY_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS,
  getActionItemName,
} from "./actionItemMeta";

const TABS_ID = "action-items-tabs";
const STAT_GRID = "grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4";

const ListSkeleton = () => (
  <div className="flex flex-col gap-3" aria-busy="true">
    {[0, 1, 2].map((key) => (
      <Skeleton key={key} className="h-24 w-full rounded-card" />
    ))}
  </div>
);

const GuestActionItems = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [prayerRequestsLoading, setPrayerRequestsLoading] = useState(true);
  const [prayerError, setPrayerError] = useState(null);
  const [activeTab, setActiveTab] = useState("action-items");
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [prayerStats, setPrayerStats] = useState({
    total: 0,
    pending: 0,
    answered: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dateSort, setDateSort] = useState("newest");
  const [prayerFilterStatus, setPrayerFilterStatus] = useState("pending");
  const [staffMembers, setStaffMembers] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedPrayers, setExpandedPrayers] = useState({});

  // The staff list is static, so load it once rather than on every filter change.
  useEffect(() => {
    fetchStaffMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchActionItems();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterStatus, filterPriority, filterType, dateSort]);

  useEffect(() => {
    fetchPrayerRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prayerFilterStatus]);

  const fetchPrayerRequests = async () => {
    try {
      setPrayerRequestsLoading(true);
      setPrayerError(null);
      const response = await heartbeatActionItemService.getPrayerRequests({
        status: prayerFilterStatus,
      });
      if (response.success) {
        setPrayerRequests(response.data || []);
        const pending = response.data?.filter((p) => !p.isAnswered).length || 0;
        const answered = response.data?.filter((p) => p.isAnswered).length || 0;
        setPrayerStats({
          total: response.pagination?.totalCount || response.data?.length || 0,
          pending,
          answered,
        });
      }
    } catch (err) {
      console.error("Error fetching prayer requests:", err);
      setPrayerError(err);
    } finally {
      setPrayerRequestsLoading(false);
    }
  };

  const fetchActionItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (searchQuery) params.q = searchQuery;
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      if (filterType) params.type = filterType;
      params.sortBy = "createdAt";
      params.sortOrder = dateSort === "newest" ? "DESC" : "ASC";

      const response = await heartbeatActionItemService.getActionItems(params);
      if (response.success) {
        setActionItems(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching action items:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await heartbeatActionItemService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const response = await AtriumApi.get("/v1/users", {
        params: { role: "staff,admin", per: 100 },
      });
      setStaffMembers(response.data.users || []);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    if (newStatus === "dismissed") {
      const confirmed = await confirm({
        title: `Dismiss action item for "${getActionItemName(item)}"?`,
        message: "It will be marked as dismissed. You can reopen it later by marking it in progress.",
        confirmLabel: "Dismiss",
      });
      if (!confirmed) return;
    }
    try {
      await heartbeatActionItemService.updateActionItem(item.id, { status: newStatus });
      notify.success("Status updated");
      fetchActionItems();
      fetchStats();
    } catch (err) {
      console.error("Error updating status:", err);
      notify.error(getApiErrorMessage(err, "Could not update status"));
    }
  };

  const closeAssignDialog = () => {
    setAssignModalOpen(false);
    setSelectedItem(null);
  };

  const handleAssign = async (itemId, assignedToId) => {
    try {
      await heartbeatActionItemService.assignActionItem(itemId, assignedToId);
      notify.success("Action item assigned");
      fetchActionItems();
      closeAssignDialog();
    } catch (err) {
      console.error("Error assigning item:", err);
      notify.error(getApiErrorMessage(err, "Could not assign action item"));
    }
  };

  const handleDelete = async (item) => {
    const confirmed = await confirm({
      title: `Delete action item for "${getActionItemName(item)}"?`,
      message: "This action item will be permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await heartbeatActionItemService.deleteActionItem(item.id);
      notify.success("Action item deleted");
      fetchActionItems();
      fetchStats();
    } catch (err) {
      console.error("Error deleting item:", err);
      notify.error(getApiErrorMessage(err, "Could not delete action item"));
    }
  };

  const handleMarkPrayerAnswered = async (prayerId, isAnswered) => {
    try {
      await heartbeatActionItemService.markPrayerRequestAnswered(prayerId, isAnswered);
      notify.success(isAnswered ? "Prayer marked as answered" : "Prayer marked as pending");
      fetchPrayerRequests();
    } catch (err) {
      console.error("Error marking prayer:", err);
      notify.error(getApiErrorMessage(err, "Could not update prayer request"));
    }
  };

  const togglePrayerExpand = (prayerId) => {
    setExpandedPrayers((prev) => ({
      ...prev,
      [prayerId]: !prev[prayerId],
    }));
  };

  const isFiltered = Boolean(searchQuery || filterStatus || filterPriority || filterType);
  const clearFilters = () => {
    setSearchQuery("");
    setFilterStatus("");
    setFilterPriority("");
    setFilterType("");
  };

  const renderActionItems = () => {
    if (loading) return <ListSkeleton />;
    if (error) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(error, "Action items could not be loaded.")}
            onRetry={() => {
              fetchActionItems();
              fetchStats();
            }}
          />
        </Card>
      );
    }
    if (actionItems.length === 0) {
      return (
        <Card padding="md">
          {isFiltered ? (
            <EmptyState
              variant="filtered"
              title="No action items match your filters"
              description="Try a different search, status, priority or type."
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  Clear Filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={MdAssignment}
              title="No Action Items Yet"
              description="Action items will appear here when guests complete surveys."
            />
          )}
        </Card>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {actionItems.map((item, index) => (
          <ActionItemRow
            key={item.id || `action-item-${index}`}
            item={item}
            onViewProfile={() => navigate(`/admin/people/${item.userId}/profile`)}
            onAssign={() => {
              setSelectedItem(item);
              setAssignModalOpen(true);
            }}
            onStatusChange={(newStatus) => handleStatusChange(item, newStatus)}
            onDelete={() => handleDelete(item)}
          />
        ))}
      </div>
    );
  };

  const renderPrayerEmptyState = () => {
    if (prayerFilterStatus === "pending") {
      return (
        <EmptyState
          icon={MdCheckCircle}
          title="All Caught Up"
          description="Every prayer request has been answered."
        />
      );
    }
    if (prayerFilterStatus === "answered") {
      return (
        <EmptyState
          variant="filtered"
          title="No answered prayer requests"
          description="Prayer requests marked as answered will appear here."
          action={
            <Button variant="secondary" onClick={() => setPrayerFilterStatus("all")}>
              View All Requests
            </Button>
          }
        />
      );
    }
    return (
      <EmptyState
        icon={MdSelfImprovement}
        title="No Prayer Requests Yet"
        description="Prayer requests will appear here when submitted."
      />
    );
  };

  const renderPrayerRequests = () => {
    if (prayerRequestsLoading) return <ListSkeleton />;
    if (prayerError) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(prayerError, "Prayer requests could not be loaded.")}
            onRetry={fetchPrayerRequests}
          />
        </Card>
      );
    }
    if (prayerRequests.length === 0) {
      return <Card padding="md">{renderPrayerEmptyState()}</Card>;
    }
    return (
      <div className="flex flex-col gap-3">
        {prayerRequests.map((prayer) => (
          <PrayerRequestRow
            key={prayer.id}
            prayer={prayer}
            expanded={expandedPrayers[prayer.id]}
            onToggleExpand={() => togglePrayerExpand(prayer.id)}
            onMarkAnswered={(isAnswered) => handleMarkPrayerAnswered(prayer.id, isAnswered)}
          />
        ))}
      </div>
    );
  };

  const tabs = [
    { id: "action-items", label: "Action Items", count: stats.pending || 0 },
    { id: "prayer-requests", label: "Prayer Requests", count: prayerStats.pending || 0 },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Action Items"
        description="Manage prayer requests and follow up on Heartbeat survey responses"
        tabs={
          <Tabs
            id={TABS_ID}
            aria-label="Action item sections"
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            syncParam="tab"
          />
        }
      />

      <TabPanel tabsId={TABS_ID} tabId={activeTab} className="flex flex-col gap-5">
        {activeTab === "prayer-requests" ? (
          <>
            <div className={STAT_GRID}>
              <StatCard
                icon={MdSelfImprovement}
                accent="gold"
                label="Pending Prayers"
                value={formatNumber(prayerStats.pending || 0)}
                loading={prayerRequestsLoading}
              />
              <StatCard
                icon={MdCheckCircle}
                accent="sage"
                label="Answered"
                value={formatNumber(prayerStats.answered || 0)}
                loading={prayerRequestsLoading}
              />
              <StatCard
                icon={MdAssignment}
                accent="slate"
                label="Total Requests"
                value={formatNumber(prayerStats.total || 0)}
                loading={prayerRequestsLoading}
              />
            </div>

            <SegmentedControl
              aria-label="Filter prayer requests"
              options={PRAYER_STATUS_OPTIONS}
              value={prayerFilterStatus}
              onChange={setPrayerFilterStatus}
              className="self-start"
            />

            {renderPrayerRequests()}
          </>
        ) : (
          <>
            <div className={STAT_GRID}>
              <StatCard
                icon={MdAssignment}
                accent="slate"
                label="Total Items"
                value={formatNumber(stats.total || 0)}
                loading={statsLoading}
              />
              <StatCard
                icon={MdAccessTime}
                accent="gold"
                label="Pending"
                value={formatNumber(stats.pending || 0)}
                loading={statsLoading}
              />
              <StatCard
                icon={MdAutorenew}
                accent="sky"
                label="In Progress"
                value={formatNumber(stats.inProgress || 0)}
                loading={statsLoading}
              />
              <StatCard
                icon={MdCheckCircle}
                accent="sage"
                label="Completed"
                value={formatNumber(stats.completed || 0)}
                loading={statsLoading}
              />
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by guest name…"
                ariaLabel="Search action items"
                className="w-full lg:flex-1"
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex">
                <Select
                  aria-label="Filter by status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  options={STATUS_FILTER_OPTIONS}
                  className="w-full lg:w-40"
                />
                <Select
                  aria-label="Filter by priority"
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  options={PRIORITY_FILTER_OPTIONS}
                  className="w-full lg:w-40"
                />
                <Select
                  aria-label="Filter by type"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  options={TYPE_FILTER_OPTIONS}
                  className="w-full lg:w-52"
                />
                <Select
                  aria-label="Sort by date"
                  value={dateSort}
                  onChange={(e) => setDateSort(e.target.value)}
                  options={DATE_SORT_OPTIONS}
                  className="w-full lg:w-40"
                />
              </div>
            </div>

            {renderActionItems()}
          </>
        )}
      </TabPanel>

      <AssignStaffDialog
        isOpen={assignModalOpen && Boolean(selectedItem)}
        itemName={selectedItem ? getActionItemName(selectedItem) : ""}
        staffMembers={staffMembers}
        onAssign={(staffId) => handleAssign(selectedItem.id, staffId)}
        onClose={closeAssignDialog}
      />
    </PageContainer>
  );
};

export default GuestActionItems;
